import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiFolder,
  FiRefreshCw,
  FiCalendar,
  FiEdit
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { categoriesAPI } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import { API_CONFIG } from '../config/api';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []); // Only fetch once on component mount

  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      const response = await categoriesAPI.getCategories(currentPage, pageSize, searchTerm);
      
      // Handle different response structures
      const data = response?.data || response || [];
      const totalPages = response?.totalPages || response?.pagination?.totalPages || 1;
      const totalItems = response?.totalItems || response?.pagination?.totalItems || 0;
      
      setCategories(Array.isArray(data) ? data : []);
      setTotalPages(totalPages);
      setTotalItems(totalItems);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
      setCategories([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoriesAPI.deleteCategory(categoryId);
        toast.success('Category deleted successfully');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      }
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleSave = async (categoryData) => {
    try {
      if (editingCategory) {
        await categoriesAPI.updateCategory(editingCategory._id, categoryData);
        toast.success('Category updated successfully');
      } else {
        await categoriesAPI.createCategory(categoryData);
        toast.success('Category created successfully');
      }
      setShowModal(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const exportToExcel = () => {
    const filteredData = categories.filter(category => {
      const matchesText = category.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                         category.description?.toLowerCase().includes(filterText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || category.isActive === (filterStatus === 'active');
      return matchesText && matchesStatus;
    });

    const ws = XLSX.utils.json_to_sheet(filteredData.map(category => ({
      Name: category.name,
      Icon: category.icon || 'N/A',
      Description: category.description || 'N/A',
      'Sort Order': category.sortOrder || 0,
      Status: category.isActive ? 'Active' : 'Inactive',
      'Created At': new Date(category.createdAt).toLocaleDateString()
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Categories');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'categories.xlsx');
    toast.success('Categories exported to Excel');
  };

  const exportToPDF = () => {
    const filteredData = categories.filter(category => {
      const matchesText = category.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                         category.description?.toLowerCase().includes(filterText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || category.isActive === (filterStatus === 'active');
      return matchesText && matchesStatus;
    });

    const doc = new jsPDF();
    doc.autoTable({
      head: [['Name', 'Icon', 'Description', 'Sort Order', 'Status', 'Created At']],
      body: filteredData.map(category => [
        category.name,
        category.icon || 'N/A',
        category.description || 'N/A',
        category.sortOrder || 0,
        category.isActive ? 'Active' : 'Inactive',
        new Date(category.createdAt).toLocaleDateString()
      ]),
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [147, 51, 234] }
    });
    doc.save('categories.pdf');
    toast.success('Categories exported to PDF');
  };

  const columns = [
    {
      name: 'Category',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            {row.icon ? (
              <img src={`${API_CONFIG.ASSETS_URL}/${row.icon}`} alt={row.name} className="w-5 h-5 rounded" />
            ) : (
              <FiFolder className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.name}</p>
            <p className="text-sm text-gray-500">{row.description}</p>
          </div>
        </div>
      ),
      width: '300px'
    },
    {
      name: 'Icon',
      selector: row => row.icon,
      sortable: false,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.icon ? (
            <img src={`${API_CONFIG.ASSETS_URL}/${row.icon}`} alt={row.name} className="w-8 h-8 rounded" />
          ) : (
            <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
              <FiFolder className="w-4 h-4 text-gray-500" />
            </div>
          )}
        </div>
      ),
      width: '80px'
    },
    {
      name: 'Sort Order',
      selector: row => row.sortOrder,
      sortable: true,
      cell: row => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row.sortOrder || 0}
        </span>
      ),
      width: '100px'
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.isActive ? (
            <FiEye className="w-4 h-4 text-green-500" />
          ) : (
            <FiEyeOff className="w-4 h-4 text-red-500" />
          )}
          <span className={row.isActive ? 'text-green-600' : 'text-red-600'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      name: 'Created',
      selector: row => row.createdAt,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          <FiCalendar className="w-4 h-4 text-gray-400" />
          <span>{new Date(row.createdAt).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <FiEdit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      width: '100px'
    }
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'transparent'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
        fontWeight: '600'
      }
    },
    rows: {
      style: {
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f8fafc'
        }
      }
    }
  };

  const filteredCategories = categories.filter(category => {
    const matchesText = category.name?.toLowerCase().includes(filterText.toLowerCase()) ||
                       category.description?.toLowerCase().includes(filterText.toLowerCase());
    const matchesStatus = filterStatus === 'all' || category.isActive === (filterStatus === 'active');
    return matchesText && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories Management</h1>
          <p className="text-gray-600 mt-1">Manage loan categories and types</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Category</span>
          </button>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search categories..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={fetchCategories}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          title=""
          columns={columns}
          data={filteredCategories}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50]}
          sortable
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          }
          noDataComponent={
            <div className="flex flex-col items-center justify-center p-8">
              <FiFolder className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No categories found</p>
            </div>
          }
        />
      </div>

      {/* Category Modal */}
      <AnimatePresence>
        {showModal && (
          <CategoryModal
            category={editingCategory}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
              setEditingCategory(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ category, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    icon: category?.icon || '',
    description: category?.description || '',
    sortOrder: category?.sortOrder || 0,
    isActive: category?.isActive ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {category ? 'Edit Category' : 'Add New Category'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon *</label>
              <ImageUpload
                currentImage={formData.icon}
                onImageChange={(imageUrl) => setFormData({ ...formData, icon: imageUrl })}
                accept="image/*"
                maxSize={2 * 1024 * 1024} // 2MB
                placeholder="Upload category icon"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter category description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {category ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Categories; 